// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useNavigate } from 'react-router-dom';


import { Action } from 'src/types';

import { useCustomerConfig } from '@store';
import { copyToClipboard } from '@services/utilityService';

import { css, keyframes } from '@emotion/css';
import { TableCell, TableRow, Theme, useTheme } from '@mui/material';
import { MOVE_TABLE_COLUMN, type MoveTableColumn } from '@features/invoices/components/moveTableColumns';
import { BiCar, BiCommentError, BiCopyAlt } from 'react-icons/bi';

import InvoiceDetailsTableFoldCharges from '@features/invoices/components/InvoiceDetailsTableFoldCharges';
import { movePath } from '@features/products/productRoutes';

// TYPES ---------------------------------------------------------------- //

type InvoiceDetailsTableRowChargesProps = {
  row: any;
  foldId: string | null;
  setFoldId: (id: string | null) => void;
  handleDisputeModalOpen: any;
  invoice?: any;
  hasMoveCharges?: boolean;
  hasDistance?: boolean;
  /** Columns the table dropped for width, so cells stay aligned with headers. */
  hiddenColumns?: ReadonlySet<MoveTableColumn>;
  hasGuestrides?: boolean;
};

// COMPONENT ---------------------------------------------------------------- //

const InvoiceDetailsTableRowCharges = ({
  row,
  foldId,
  setFoldId,
  handleDisputeModalOpen,
  invoice,
  hasMoveCharges,
  hasDistance,
  hiddenColumns = new Set<MoveTableColumn>(),
  hasGuestrides,
}: InvoiceDetailsTableRowChargesProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  const customerConfig = useCustomerConfig();
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';

  const navigate = useNavigate();

  const moveGroup = row.moveGroup || {};
  const move = moveGroup.move || {};
  const isGuestride = moveGroup.sourceKind === 'guestride';

  // Handle fold row state — uses groupKey ('move-123' / 'guestride-456')
  const handleFoldRow = (key: string) => {
    const newFoldId = key === foldId ? null : key;
    if (newFoldId === null) {
      localStorage.removeItem(`invoice-fold-group-key`);
    } else {
      localStorage.setItem(`invoice-fold-group-key`, newFoldId);
    }
    setFoldId(newFoldId);
  };

  // Set a list of cells to render the row
  const rowCells = [
    {
      value: row.MOVE_ID || `-`,
      align: `left`,
      onClick: () => handleFoldRow(moveGroup.groupKey),
    },
    {
      value: row.REF_NUM || `-`,
      align: `left`,
      onClick: () => handleFoldRow(moveGroup.groupKey),
    },
    {
      value: row.MOVE_DATE || `-`,
      align: `left`,
      onClick: () => handleFoldRow(moveGroup.groupKey),
    },
    {
      value: row.GUEST_NAME || `-`,
      align: `left`,
      onClick: () => handleFoldRow(moveGroup.groupKey),
      hide: !hasGuestrides,
    },
    {
      value: row.LANE || `-`,
      align: `left`,
      onClick: () => handleFoldRow(moveGroup.groupKey),
      hide: hiddenColumns.has(MOVE_TABLE_COLUMN.LANE),
    },
    {
      value: row.DLV_REP || `-`,
      align: `left`,
      onClick: () => handleFoldRow(moveGroup.groupKey),
      hide: invoice?.customer?.id !== 2,
    },
    {
      value: row.DISTANCE || `-`,
      align: `left`,
      onClick: () => handleFoldRow(moveGroup.groupKey),
      hide: !hasDistance || hiddenColumns.has(MOVE_TABLE_COLUMN.DISTANCE),
    },
    {
      value: row.WEIGHT_CLASS || `-`,
      align: `left`,
      onClick: () => handleFoldRow(moveGroup.groupKey),
      hide: !hasMoveCharges || hiddenColumns.has(MOVE_TABLE_COLUMN.WEIGHT_CLASS),
    },
    {
      value: row.COST || `-`,
      align: `right`,
      onClick: () => handleFoldRow(moveGroup.groupKey),
    },
  ];

  // Check if any charges have credits (similar to disputed in old system)
  const hasCredits = moveGroup.credits && moveGroup.credits.length > 0;

  // Set a list of actions after right-clicking on a row
  const guestride = moveGroup.guestride;
  const refNum = isGuestride ? guestride?.reference_num : move?.reference_num;
  const actions: Action[] = [
    {
      label: `Go To Move Details`,
      icon: <BiCar className={cls.menuIcon} />,
      handler: () => navigate(movePath(move?.id)),
      hide: isGuestride,
    },
    // Note: Dispute functionality would need to be implemented for charge-based system
    // For now, keeping it disabled
    {
      label: `Dispute Charges`,
      icon: <BiCommentError className={cls.menuIcon} />,
      handler: () => {}, // TODO: Implement dispute for charges
      disabled: true, // Disabled until dispute system is implemented for charges
    },
    {
      label: `Copy ${refLabel}`,
      icon: <BiCopyAlt className={cls.menuIcon} />,
      handler: () => copyToClipboard(refNum),
      hide: !refNum || (move?.customer && move?.customer?.id !== 2),
    },
    {
      label: `Copy Delivery Timestamp`,
      icon: <BiCopyAlt className={cls.menuIcon} />,
      handler: () => copyToClipboard(move?.delivery_successful),
      hide: isGuestride || !move?.delivery_successful,
    },
  ];

  return (
    <>
      <TableRow sx={clsx.row}>
        {rowCells.map((cell, i) => {
          if (!cell.hide)
            return (
              <React.Fragment key={`invoice-cell-${moveGroup.moveId}-${i}`}>
                <TableCell
                  sx={hasCredits ? clsx.cellDisputed : clsx.cell}
                  align={cell.align || `left`}
                  onClick={cell.onClick || undefined}
                >
                  {cell.value || null}
                </TableCell>
              </React.Fragment>
            );
          else return null;
        })}
      </TableRow>

      {foldId === moveGroup?.groupKey ? (
        <TableRow sx={clsx.foldRow}>
          <TableCell sx={clsx.foldCell} colSpan={rowCells?.length || 0}>
            <div className={cls.fold}>
              <InvoiceDetailsTableFoldCharges moveGroup={moveGroup} actions={actions} invoice={invoice} />
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => {
  const fadeIn = keyframes`
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  `;

  return {
    fold: css`
      animation: ${fadeIn} 0.667s;
    `,

    menuIcon: css`
      display: block;
      margin-top: -1px;
      margin-right: 8px;
    `,
  };
};

const useSxStyles = (theme?: Theme) => ({
  row: {
    background: theme?.palette?.background?.paper,
    '&:hover, &:active': {
      background: `${theme?.palette?.action?.hover} !important`,
    },
    cursor: 'pointer',
    transition: '0.1s',
  },

  cell: {
    color: theme?.palette?.text?.primary,
    transition: `0.1s`,
  },
  cellDisputed: {
    color: theme?.palette?.text?.disabled,
    transition: `0.1s`,
  },

  foldRow: {
    borderBottom: `1px solid ${theme?.palette?.divider}`,
  },
  foldCell: {
    padding: '0 !important',
    border: 'none',
  },
});

// EXPORT ---------------------------------------------------------------- //

export default InvoiceDetailsTableRowCharges;
